import clsx from 'clsx';
import { Dropdown } from 'components';
import { useMemo, useCallback, useEffect } from 'react';
import {
  getCHWsForFilters,
  getProgramsForFilters,
  getClientsStatuses
} from 'store/clients/clientsActions';
import { updateFilters, CHWItem, ProgramsItem, FilterState } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useSyncPageWithSessionStorage from 'utils/hooks/useClientPageSync';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { STATUSES_FOR_OUTREACH } from 'modules/header/header';

import style from './filters.module.scss';

const categoryList = [
  {
    id: 'ACTIVE',
    label: 'Active'
  },
  {
    id: 'INACTIVE',
    label: 'Inactive'
  }
];

export const getFiltersString = (filters: Partial<FilterState>) => {
  const statuses = new URLSearchParams();

  if (filters.statuses?.length) {
    statuses.set('statuses', filters.statuses.join(','));
  }

  if (filters.programIds?.length) {
    statuses.set('programIds', filters.programIds.join(','));
  }

  if (filters.chwIds?.length) {
    statuses.set('chwIds', filters.chwIds.join(','));
  }

  if (filters.action) {
    statuses.set('action', filters.action);
  }

  return statuses.toString();
};

const Filters = ({
  scrollContainerRef
}: {
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { isMobile } = useResponsive();
  const dispatch = useAppDispatch();
  const { resetSavedPage } = useSyncPageWithSessionStorage();
  const { enableBillingUpdates } = useFlags();

  const { filters, searchText, statusesList, programsListForFilters, chwListForFilters } =
    useAppSelector((state) => state.clients);

  const filteredStatuses = useMemo(() => {
    if (filters?.action === 'OUTREACH') {
      return statusesList?.filter((item) => STATUSES_FOR_OUTREACH.includes(item.id));
    }
    if (Boolean(filters?.action)) {
      return statusesList?.filter((item) => item.id === 'ENROLLED');
    }
    return statusesList;
  }, [filters?.action, statusesList]);

  useEffect(() => {
    dispatch(getProgramsForFilters());
    dispatch(getCHWsForFilters());
    dispatch(getClientsStatuses());
  }, []);

  const getData = useCallback(async () => {
    const filteredPrograms = filters.programIds?.filter((pid: string) =>
      programsListForFilters.find((program: ProgramsItem) => program.id.toString() === pid)
    );
    const filteredChws = filters.chwIds?.filter((uid: string) =>
      chwListForFilters.find((chw: CHWItem) => chw.userId.toString() === uid)
    );
    if (
      filteredChws?.length !== filters.chwIds?.length ||
      filteredPrograms?.length !== filters.programIds?.length
    ) {
      dispatch(
        updateFilters({
          programIds: filteredPrograms,
          chwIds: filteredChws
        })
      );
    }
  }, [dispatch, filters.programIds, filters.chwIds]);

  useEffect(() => {
    getData();
  }, [getData]);

  const CHWList = useMemo(
    () =>
      chwListForFilters.map((item) => ({
        ...item,
        id: item.userId.toString(),
        name: item.name,
        category: item.active ? 'ACTIVE' : 'INACTIVE'
      })),
    [chwListForFilters]
  );

  const onFilterChange = (payload: Partial<FilterState>) => {
    resetSavedPage();
    scrollContainerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
    dispatch(updateFilters(payload));
  };

  return (
    <>
      <div className={style.filter}>
        <Dropdown
          value={filters.statuses}
          className={clsx(
            filters?.statuses?.length > 0 ? style.selected : undefined,
            style.filterDropdown
          )}
          data={filteredStatuses}
          onChange={(e) => {
            onFilterChange({
              ...filters,
              statuses: e.target.value as string[]
            });
          }}
          label="Status"
          multiSelect
          rounded
          categoryList={categoryList}
          disabled={searchText.length > 0}
          dropdownFullWidth={isMobile}
        />
      </div>
      <div className={style.filter}>
        <Dropdown
          value={filters.programIds ?? []}
          data={programsListForFilters.map((program) => ({
            id: program.id?.toString() ?? '',
            name: program.name ?? '',
            category: enableBillingUpdates ? (program.active ? 'ACTIVE' : 'INACTIVE') : undefined
          }))}
          onChange={(e) => {
            onFilterChange({
              ...filters,
              programIds: e.target.value as string[]
            });
          }}
          label="Program"
          multiSelect
          rounded
          className={clsx(
            filters.programIds?.length > 0 ? style.selected : undefined,
            style.filterDropdown
          )}
          withSearch
          disabled={searchText.length > 0}
          dropdownFullWidth={isMobile}
          categoryList={enableBillingUpdates ? categoryList : undefined}
        />
      </div>
      <div className={style.filter}>
        <Dropdown
          value={filters.chwIds ?? []}
          className={clsx(
            filters.chwIds?.length > 0 ? style.selected : undefined,
            style.filterDropdown
          )}
          data={CHWList}
          onChange={(e) => {
            onFilterChange({
              ...filters,
              chwIds: e.target.value as string[]
            });
          }}
          label="CHW"
          multiSelect
          categoryList={categoryList}
          rounded
          withSearch
          disabled={searchText.length > 0}
          dropdownFullWidth={isMobile}
        />
      </div>
    </>
  );
};

export default Filters;
